// Task #1664 — Card preview entità cross-modulo (lead/deal/contact/task/event/file).
// Usata in chat (MessageItem) e feed (PostCard) per render delle mention
// `@kind/<uuid>`. TanStack cache 60s. Click → navigate al modulo target.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  UserPlus, Trophy, User, CheckSquare, Calendar, FileText, ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchEntityPreview } from '@/lib/cross-bridge';
import type { CrossEntityKind, CrossEntityPreview } from '@easyflow/shared';

const KIND_ICON_MAP: Record<CrossEntityKind, React.ComponentType<{ className?: string }>> = {
  lead: UserPlus,
  deal: Trophy,
  contact: User,
  task: CheckSquare,
  event: Calendar,
  file: FileText,
};

const KIND_LABEL: Record<CrossEntityKind, string> = {
  lead: 'Lead',
  deal: 'Deal',
  contact: 'Contatto',
  task: 'Task',
  event: 'Evento',
  file: 'File',
};

interface Props {
  kind: CrossEntityKind;
  id: string;
  /** Preview pre-fetched (es. da feed_posts.content_json.shared_entity). Salta la query. */
  preview?: CrossEntityPreview | null;
  compact?: boolean;
  className?: string;
}

export function EntityCard({ kind, id, preview, compact, className }: Props) {
  const enabled = !preview;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cross-entity', kind, id],
    queryFn: () => fetchEntityPreview(kind, id),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const entity = preview ?? data;
  const Icon = KIND_ICON_MAP[kind];

  if (isLoading) {
    return (
      <div className={cn(
        'inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border bg-gray-50 dark:bg-gray-900/50 animate-pulse',
        className,
      )}>
        <Icon className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs text-gray-400">Caricamento {KIND_LABEL[kind].toLowerCase()}…</span>
      </div>
    );
  }

  if (isError || !entity) {
    return (
      <span className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed text-xs text-muted-foreground',
        className,
      )}>
        <Icon className="h-3 w-3" /> @{kind}/{id.slice(0, 8)}…
      </span>
    );
  }

  if (compact) {
    return (
      <Link
        to={entity.url}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors no-underline',
          className,
        )}
        style={{ borderColor: entity.accent, color: entity.accent }}
        title={entity.subtitle ?? undefined}
      >
        <Icon className="h-3 w-3" />
        <span className="truncate max-w-[160px]">{entity.title}</span>
      </Link>
    );
  }

  return (
    <Link
      to={entity.url}
      className={cn(
        'group flex items-start gap-3 p-3 rounded-lg border bg-white dark:bg-gray-900/50 hover:shadow-md transition-all no-underline max-w-md',
        className,
      )}
      style={{ borderLeftColor: entity.accent, borderLeftWidth: 3 }}
    >
      <div
        className="h-9 w-9 rounded-md flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${entity.accent}1a`, color: entity.accent }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-semibold" style={{ color: entity.accent }}>
          {KIND_LABEL[entity.kind]}
        </div>
        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:underline">
          {entity.title}
        </div>
        {entity.subtitle && (
          <div className="text-xs text-muted-foreground truncate">{entity.subtitle}</div>
        )}
      </div>
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </Link>
  );
}
